import { useEffect, useState } from 'react'

import { UseFormMethods } from 'react-hook-form'
import { HiOutlineOfficeBuilding } from 'react-icons/hi'

import { Box, FormErrorMessage, FormLabel, HStack } from '@chakra-ui/react'

import {
  AddressSearch,
  FormInputControl,
  FormInputSelectControl,
  PinMarker,
  StaticMapBox,
} from '@/components/ui'
import { StyledFormInputControl } from '@/components/ui'
import { FacilityType } from '@/graphql/generated/schemas'

import {
  FormInputsIProps,
  LabelValueIProps,
  LatLngStateIProps,
} from '../types/types'
import {
  latRegex,
  lonRegex,
  validateLatitude,
  validateLongitude,
} from '../utils/utils'

interface CreateFacilityFormIProps {
  form: UseFormMethods<FormInputsIProps>
  facilityTypes: LabelValueIProps[]
  isOpen: boolean
}

export const CreateFacilityForm = ({
  form,
  facilityTypes,
  isOpen,
}: CreateFacilityFormIProps) => {
  const {
    errors,
    control,
    trigger,
    register,
    getValues,
    setValue,
    unregister,
    reset,
    watch,
  } = form
  const type = watch('type')?.value
  const { latitude, longitude } = watch(['latitude', 'longitude', 'address'])

  const [latLng, setLatLng] = useState<LatLngStateIProps | null>(null)
  const [shouldShowMap, setShouldShowMap] = useState(false)

  useEffect(() => {
    if (type === FacilityType.Market) {
      handleMarketForm()
    } else {
      register('address', { required: true })
      register('latitude', { required: true })
      register('longitude', { required: true })
    }
    trigger(['address'])
  }, [type])

  const handleMarketForm = () => {
    reset({ ...getValues(), latitude: null, longitude: null, address: null })
    setShouldShowMap(false)
    unregister(['address', 'latitude', 'longitude'])
  }

  useEffect(() => {
    if (latRegex.test(latitude) && lonRegex.test(longitude)) {
      setLatLng({
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      })
      setShouldShowMap(true)
    }
  }, [latitude, longitude, type])

  useEffect(() => {
    if (!isOpen) {
      reset()
      setShouldShowMap(false)
      setLatLng(null)
    }
  }, [isOpen])

  return (
    <form>
      <Box px='6'>
        <Box mb='3'>
          <FormInputControl
            errorMessage={errors.name && errors.name.message}
            id='name'
            inputRef={register({
              required: 'Facility Name is required',
            })}
            isInvalid={!!errors.name}
            label='Facility Name'
            placeholder='Enter the Facility name'
          />
        </Box>
        <Box mb='3'>
          <FormInputControl
            errorMessage={errors.shortName && errors.shortName.message}
            id='shortName'
            inputRef={register({
              required: 'Facility Initials are required',
              maxLength: {
                value: 8,
                message: 'Must be 8 characters or less',
              },
            })}
            isInvalid={!!errors.shortName}
            label='Short Name'
            placeholder='Enter the Facility initials'
          />
        </Box>
        <Box mb='3'>
          <FormInputSelectControl
            control={control}
            dataTestId='createFacility_facilityName'
            defaultValue={null}
            id='type'
            isInvalid={!!errors.type}
            label='Facility Type'
            options={facilityTypes}
            placeholder='Select the Facility type'
            rules={{ required: true }}
          />
        </Box>

        <StyledFormInputControl mb='4'>
          <FormLabel>Address</FormLabel>
          <AddressSearch
            defaultValue={null}
            disabled={type === FacilityType.Market}
            onSelectAddress={(address) => {
              setValue('address', address?.value)
              if (address) {
                setValue('latitude', address?.latitude)
                setValue('longitude', address?.longitude)
                setLatLng({
                  lat: address?.latitude,
                  lng: address?.longitude,
                })
                setShouldShowMap(true)
                trigger(['address', 'latitude', 'longitude'])
              }
            }}
            showTooltip={false}
          />
          <FormErrorMessage>
            {errors.address && errors.address.message}
          </FormErrorMessage>
        </StyledFormInputControl>

        <HStack align='stretch' mb='4' spacing='3'>
          <FormInputControl
            disabled={type === FacilityType.Market}
            errorMessage={errors.latitude && errors.latitude.message}
            id='latitude'
            inputRef={register({
              validate: (v) => validateLatitude(v, type),
            })}
            isInvalid={Boolean(errors.latitude)}
            label='Latitude'
            placeholder='Latitude'
          />
          <FormInputControl
            disabled={type === FacilityType.Market}
            errorMessage={errors.longitude && errors.longitude.message}
            id='longitude'
            inputRef={register({
              validate: (v) => validateLongitude(v, type),
            })}
            isInvalid={Boolean(errors.longitude)}
            label='Longitude'
            placeholder='Longitude'
          />
        </HStack>
      </Box>

      {shouldShowMap && (
        <StaticMapBox
          height='150px'
          latitude={latLng?.lat}
          longitude={latLng?.lng}
        >
          <PinMarker
            Icon={HiOutlineOfficeBuilding}
            bgGradient='linear(to-r, #1CFDE5, #07DB94)'
            borderColor='#42CD70'
            fadedPulseBoxShadow='rgba(43, 210, 180, 0)'
            pulseBoxShadow='rgba(43, 210, 180, 0.4)'
          />
        </StaticMapBox>
      )}
    </form>
  )
}
